// @ts-strict-ignore
import {
	AfterContentInit,
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	contentChildren,
	inject,
	input,
	TemplateRef,
	viewChild,
} from "@angular/core";
import {AdministrationTableService} from "../administration-table.service";
import {TrackByMethods} from "@services";
import {Translations} from "@translations";
import {DataPermissions, IId} from "@models";
import {MatColumnDef, MatTable} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {toObservable} from "@angular/core/rxjs-interop";
import {take} from "rxjs/operators";

@UntilDestroy()
@Component({
	selector: "sim-administration-table",
	templateUrl: "./administration-table.component.html",
	styleUrl: "./administration-table.component.scss",
	hostDirectives: [MatSort],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AdministrationTableComponent<T extends IId> implements AfterContentInit, AfterViewInit {
	protected readonly T = Translations;
	protected readonly TrackByMethods = TrackByMethods;
	protected readonly DataPermissions = DataPermissions;

	protected readonly stateService = inject<AdministrationTableService<T>>(AdministrationTableService);
	private _matSort = inject(MatSort, {self: true});

	protected readonly expandStartIcon = {
		fontSet: "fad",
		fontIcon: "fa-chevron-down",
		themeColor: "accent",
	} as const;
	protected readonly expandEndIcon = {
		fontSet: "fad",
		fontIcon: "fa-chevron-up",
		themeColor: "accent",
	} as const;

	protected table = viewChild<MatTable<T>>(MatTable);
	protected columnDefs = contentChildren(MatColumnDef);

	public isSelectionEnabled = input(true);
	public isExpandable = input(false);
	public displayedColumns = input.required<string[]>();

	public actionTemplate = input<TemplateRef<{$implicit: T}>>();

	constructor() {
		// we listen for the first sortColumn change, this either comes from the url or the default value
		// we need to update the matSort to reflect this change
		toObservable(this.stateService.sortColumn)
			.pipe(take(1))
			.subscribe((sortColumn) => {
				this._matSort.sort({id: sortColumn, start: this.stateService.sortDirection(), disableClear: false});
			});
	}

	public ngAfterContentInit() {
		this.columnDefs().forEach((columnDef) => this.table().addColumnDef(columnDef));
	}

	public ngAfterViewInit() {
		// we listen for the sort change initiated by the user and update the state
		this._matSort.sortChange.pipe(untilDestroyed(this)).subscribe((sort) => this.stateService.sortChanged(sort));
	}

	protected toggleExpandElement(element: T) {
		if (this.isExpandable()) {
			this.stateService.toggleExpandElement(element.id);
		}
	}
}
